'use client';

import { ReleaseNoteDocumentView, ReleaseNoteItemView } from 'bff';
import { useTranslations } from 'i11n';
import { ReactNode } from 'react';
import { cn } from 'ui';
import {
  editorialBody,
  editorialColumn,
  editorialFontVariable,
  editorialLink,
  editorialParagraph,
  editorialSerif,
  editorialSubhead,
} from './editorial';
import { readingMinutes } from './readingTime';
import { formatPublishedDate } from './releaseNoteDates';
import { ReleaseNoteKindBadge } from './ReleaseNoteKindBadge';
import {
  ReleaseNoteFigure,
  ReleaseNoteSection,
  ReleaseNoteSectionBody,
} from './ReleaseNoteSection';
import { RichText } from './RichText';

// Marks each item's element, so a page can tell which one is on screen.
export const RELEASE_NOTE_ITEM_ATTRIBUTE = 'data-release-note-item';

const ENDS_WITH_PUNCTUATION = /[.:;!?]$/;

const DROP_CAP =
  '[&>p:first-child]:first-letter:float-left [&>p:first-child]:first-letter:pr-2.5 [&>p:first-child]:first-letter:pt-1.5 [&>p:first-child]:first-letter:text-[5.1em] [&>p:first-child]:first-letter:font-semibold [&>p:first-child]:first-letter:leading-[0.8]';

// "Estado del equipo." as the bold lead-in of a summary line.
const leadIn = (title: string) =>
  ENDS_WITH_PUNCTUATION.test(title) ? title : `${title}.`;

const Byline = ({
  note,
  meta,
}: {
  note: ReleaseNoteDocumentView;
  meta?: ReactNode;
}) => {
  const t = useTranslations();
  const minutes = readingMinutes(note);

  return (
    <div className='flex flex-col gap-3 border-t border-stone-200 pt-3 text-[0.8125rem] text-muted-foreground sm:flex-row sm:items-end sm:justify-between'>
      <div className='flex flex-col gap-0.5'>
        <span className='text-sm font-semibold text-foreground'>
          {t('release-notes.article.byline')}
        </span>
        <span className='flex flex-wrap items-center gap-x-1.5 gap-y-1'>
          {note.published_at && (
            <>
              <time dateTime={note.published_at}>
                {formatPublishedDate(note.published_at)}
              </time>
              <span aria-hidden>·</span>
            </>
          )}
          {t(
            minutes === 1
              ? 'release-notes.article.reading-time.one'
              : 'release-notes.article.reading-time.many',
            { count: minutes },
          )}
          <ReleaseNoteKindBadge kind={note.kind} quiet />
        </span>
      </div>
      {meta && <div className='flex flex-wrap items-center gap-3'>{meta}</div>}
    </div>
  );
};

// "Lo que hay que saber": each novelty in a line, a jump to where it is told.
const MustKnow = ({ items }: { items: ReleaseNoteItemView[] }) => {
  const t = useTranslations();
  const features = items.filter((item) => item.kind === 'feature');

  if (!features.length) return null;

  return (
    <aside
      aria-label={t('release-notes.article.must-know')}
      className={cn(
        editorialColumn,
        'mb-2 border-y border-b-stone-200 border-t-foreground pb-1.5 pt-4',
      )}
    >
      <h2 className='mb-2 text-[0.9375rem] font-bold text-foreground'>
        {t('release-notes.article.must-know')}
      </h2>
      <ol className='list-decimal pl-5 marker:font-sans marker:text-sm marker:font-bold marker:text-primary'>
        {features.map((item) => (
          <li
            key={item.id}
            className={cn(
              editorialSerif,
              'mb-2.5 pl-1 text-[1.0625rem] leading-[1.45] text-foreground md:text-lg',
            )}
          >
            <a href={`#${item.id}`} className={editorialLink}>
              <b className='font-semibold'>{leadIn(item.title)}</b>
              {item.summary && ` ${item.summary}`}
            </a>
          </li>
        ))}
      </ol>
    </aside>
  );
};

const itemAnchor = (item: ReleaseNoteItemView) => ({
  id: item.id,
  [RELEASE_NOTE_ITEM_ATTRIBUTE]: item.id,
});

// An overview reads as one figure: its title over the sections, its
// description as the caption.
const OverviewItem = ({ item }: { item: ReleaseNoteItemView }) => (
  <section {...itemAnchor(item)} className='scroll-mt-20'>
    <ReleaseNoteFigure
      title={item.title}
      caption={<RichText text={item.description} className='leading-[1.45]' />}
    >
      {item.sections.map((section) => (
        <ReleaseNoteSectionBody key={section.id} section={section} />
      ))}
    </ReleaseNoteFigure>
  </section>
);

// A novelty as a chapter: its title as a subhead, its description as the copy
// that opens it, its sections in between the column and wider figures.
const FeatureItem = ({
  item,
  isFirst,
}: {
  item: ReleaseNoteItemView;
  isFirst: boolean;
}) => (
  <section {...itemAnchor(item)} className='scroll-mt-20'>
    <div className={editorialColumn}>
      <h2
        className={cn(
          editorialSubhead,
          'mb-[0.55em] mt-[1.9em] text-[1.5625rem] md:text-[1.875rem]',
        )}
      >
        {item.title}
      </h2>
      <div className={cn(editorialBody, isFirst && DROP_CAP)}>
        <RichText text={item.description} className={editorialParagraph} />
      </div>
    </div>
    {item.sections.map((section) => (
      <ReleaseNoteSection key={section.id} section={section} />
    ))}
  </section>
);

// A note as a newspaper article: kicker, headline, deck and byline; what to
// know; the items as chapters. The reader's article and the backoffice
// preview render this same document and add what is theirs: `meta` joins the
// byline, `header` closes the head, `children` come after the last item.
export const ReleaseNoteDocument = ({
  note,
  meta,
  header,
  children,
}: {
  note: ReleaseNoteDocumentView;
  meta?: ReactNode;
  header?: ReactNode;
  children?: ReactNode;
}) => {
  const [firstFeature] = note.items.filter((item) => item.kind === 'feature');

  return (
    <article className={cn(editorialFontVariable, 'min-w-0')}>
      <header className={cn(editorialColumn, 'mb-9 flex flex-col')}>
        {note.audience && (
          <p className='mb-3.5 text-sm font-bold text-primary'>
            {note.audience}
          </p>
        )}
        <h1
          className={cn(
            editorialSerif,
            'text-[2.125rem] font-semibold leading-[1.07] tracking-[-0.012em] text-foreground md:text-[3.25rem]',
          )}
        >
          {note.title}
        </h1>
        <p
          className={cn(
            editorialSerif,
            'mb-6 mt-4 text-[1.1875rem] font-light leading-[1.4] text-stone-600 md:text-[1.4375rem]',
          )}
        >
          {note.subtitle}
        </p>
        <Byline note={note} meta={meta} />
        {header && <div className='mt-4'>{header}</div>}
      </header>

      <MustKnow items={note.items} />

      {note.items.map((item) =>
        item.kind === 'overview' ? (
          <OverviewItem key={item.id} item={item} />
        ) : (
          <FeatureItem
            key={item.id}
            item={item}
            isFirst={item.id === firstFeature?.id}
          />
        ),
      )}

      {children && (
        <footer className={cn(editorialColumn, 'mt-14 flex flex-col gap-12')}>
          {children}
        </footer>
      )}
    </article>
  );
};
